import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface Message {
  id: string;
  senderName: string;
  senderPhoto?: string; 
  text: string;
  isExpert: boolean;
  isSystem?: boolean;
}

interface LiveStreamChatProps {
  isExpertMode: boolean;
  expertName: string; 
  userName?: string;  
  userProfilePhoto?: string; 
  channelName: string;
  onStreamEnd?: () => void;
  onRegisterEndTrigger?: (fn: () => void) => void; 
  // 🟢 Added listeners for live stats
  onViewersUpdate?: (count: number) => void;
  onLikesUpdate?: (count: number) => void;
  onRegisterToggleLike?: (fn: (action: 'like' | 'unlike') => void) => void;
}

export const LiveStreamChat = ({ 
  isExpertMode, expertName, userName, userProfilePhoto, channelName, 
  onStreamEnd, onRegisterEndTrigger, onViewersUpdate, onLikesUpdate, onRegisterToggleLike 
}: LiveStreamChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  const socketRef = useRef<Socket | null>(null);

  const onStreamEndRef = useRef(onStreamEnd);
  const onRegisterEndTriggerRef = useRef(onRegisterEndTrigger);
  const onViewersUpdateRef = useRef(onViewersUpdate);
  const onLikesUpdateRef = useRef(onLikesUpdate);

  useEffect(() => {
    onStreamEndRef.current = onStreamEnd;
    onRegisterEndTriggerRef.current = onRegisterEndTrigger;
    onViewersUpdateRef.current = onViewersUpdate;
    onLikesUpdateRef.current = onLikesUpdate;
  }, [onStreamEnd, onRegisterEndTrigger, onViewersUpdate, onLikesUpdate]);
  
  const rawName = userName || expertName || (isExpertMode ? 'Expert' : 'Guest User');
  const displayName = isExpertMode ? `${rawName} (Host)` : rawName;

  useEffect(() => {
    if (!channelName) return;

    socketRef.current = io(import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000', {
      transports: ['websocket'],
    });

    socketRef.current.emit('join_livestream', {
      channelName,
      userName: displayName,
      isExpert: isExpertMode,
    });

    socketRef.current.on('livestream_chat_message', (msg: Message) => {
      setMessages(prev => [...prev, msg]);
    });

    socketRef.current.on('livestream_ended', () => {
      if (onStreamEndRef.current) onStreamEndRef.current();
    });

    // 🟢 Listen for viewers update from backend
    socketRef.current.on('livestream_viewers_updated', (data: { viewers: number }) => {
      if (onViewersUpdateRef.current) onViewersUpdateRef.current(data.viewers);
    });

    // 🟢 Listen for likes update from backend
    socketRef.current.on('livestream_likes_updated', (data: { likes: number }) => {
      if (onLikesUpdateRef.current) onLikesUpdateRef.current(data.likes);
    });

    if (onRegisterEndTriggerRef.current) {
      onRegisterEndTriggerRef.current(() => {
        socketRef.current?.emit('livestream_ended', { channelName });
      });
    }

    // 🟢 Register the toggle like function
    if (onRegisterToggleLike) {
      onRegisterToggleLike((action: 'like' | 'unlike') => {
        socketRef.current?.emit('toggle_like', { channelName, action });
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_livestream', { channelName });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [channelName, displayName, isExpertMode]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !socketRef.current) return;

    socketRef.current.emit('livestream_chat_message', {
      channelName,
      senderName: displayName,
      senderPhoto: userProfilePhoto, 
      text: newMessage.trim(),
      isExpert: isExpertMode,
    });

    setNewMessage('');
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border-l border-gray-200">
      
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-gray-800 font-semibold flex items-center gap-2">
          <span>Live Chat</span>
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5 bg-white">
        {messages.map((msg, index) => (
          <div key={msg.id || index} className="flex flex-col">
            
            {msg.isSystem && (
              <div className="text-center w-full my-2">
                <span className="bg-gray-100 text-gray-500 text-xs italic px-3 py-1 rounded-full shadow-sm">
                  {msg.text}
                </span>
              </div>
            )}

            {!msg.isSystem && (
              <div className="flex items-start gap-3 w-full">
                
                <div className="w-8 h-8 rounded-full bg-blue-100 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                  {msg.senderPhoto ? (
                    <img src={msg.senderPhoto} alt={msg.senderName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-blue-700 font-bold text-xs">
                      {msg.senderName?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>

                <div className="flex flex-col max-w-[85%]">
                  <span className="font-semibold text-xs mb-1 text-gray-700 flex items-center gap-1.5">
                    {msg.senderName}
                    {msg.isExpert && !msg.senderName.includes('(Host)') && (
                      <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase font-bold tracking-wider">
                        Host
                      </span>
                    )}
                  </span>
                  
                  <span className={`px-3.5 py-2 text-sm break-words shadow-sm ${
                    msg.isExpert 
                      ? 'bg-blue-50 border border-blue-100 text-blue-900 rounded-2xl rounded-tl-sm' 
                      : 'bg-gray-100 border border-gray-100 text-gray-800 rounded-2xl rounded-tl-sm'
                  }`}>
                    {msg.text}
                  </span>
                </div>

              </div>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={isExpertMode ? "Say something to your audience..." : "Chat publicly..."}
            className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 transition-all placeholder-gray-400 shadow-sm"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 disabled:hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md flex items-center justify-center shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};